import React, { useEffect, useState } from 'react';
import { AIWorker } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface Props {
  workers: AIWorker[];
}

// Header pill — total $ spend across all completed tasks plus the
// running cost of every active task right now. Pulls the
// completed-tasks dollar sum from /api/analytics/summary (refreshed
// every 30s) and adds in-flight workers' currentTask.costUsd from
// the WS-fed live state. Hovering shows the split.
export const SpendIndicator: React.FC<Props> = ({ workers }) => {
  const [historic, setHistoric] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // /api/analytics/spend sums task_telemetry.cost_usd across
        // EVERY task — phase-attached or freeform. Was previously
        // pulling /phases which silently dropped freeform tasks
        // (no phase_id), so the pill showed $0 even after a $6+
        // run. New endpoint returns { totalCostUsd, taskCount, … }.
        const res = await fetch(`${API}/api/analytics/spend`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setHistoric(Number(data.totalCostUsd) || 0);
      } catch {
        /* ignore — pill silently shows the live portion only */
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Sum currentTask.costUsd across every working agent. Updates
  // every time a 'task:tokens' WS event arrives (workers prop is
  // re-rendered by the parent).
  const live = workers.reduce(
    (s, w) => s + (w.currentTask && w.currentTask.costUsd ? w.currentTask.costUsd : 0),
    0,
  );
  const total = (historic ?? 0) + live;

  if (total === 0 && live === 0 && historic === null) {
    // Nothing to show until either history loads or a task fires
    // its first usage event. Suppress the empty pill so the header
    // doesn't twitch on cold load.
    return null;
  }

  const fmt = (n: number) =>
    n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')}`;

  return (
    <span
      className={`spend-pill ${live > 0 ? 'spend-pill-live' : ''}`}
      title={`history: ${fmt(historic ?? 0)} · live: ${fmt(live)}`}
    >
      <span className="spend-pill-label">SPEND</span>
      <span className="spend-pill-value">{fmt(total)}</span>
      {live > 0 && <span className="spend-pill-dot" aria-hidden />}
    </span>
  );
};
